// src/pages/RetailerManagement.jsx
import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { PlusCircle, ArrowLeft, Users } from 'lucide-react';
import RetailerList from '../components/partners/RetailerList';
import RetailerForm from '../components/partners/RetailerForm';
import RetailerDetails from '../components/partners/RetailerDetails';
import useAuth from '../hooks/useAuth';
import { useLocation } from 'react-router-dom';

const RetailerManagement = () => {
    const { isAdmin, isManager, isSalesRep, user } = useAuth();
    const [view, setView] = useState('list');
    const [selectedRetailerId, setSelectedRetailerId] = useState(null);

    const canModify = isAdmin || isManager || isSalesRep;

    const handleAddRetailer = () => {
        setSelectedRetailerId(null);
        setView('form');
    };

    const handleEditRetailer = (retailerId) => {
        setSelectedRetailerId(retailerId);
        setView('form');
    };

    const handleViewRetailer = (retailerId) => {
        setSelectedRetailerId(retailerId);
        setView('details');
    };

    const handleAssignCommercial = (retailerId) => {
        // Cette fonction ne sera utilisée que par les admins et managers
        setSelectedRetailerId(retailerId);
        setView('assign');
    };

    const handleCancel = () => {
        setView('list');
        setSelectedRetailerId(null);
    };

    const location = useLocation();
    useEffect(() => {
        handleCancel(); // remet view='list' + selectedProductId=null
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    const handleSuccess = () => {
        setView('list');
        setSelectedRetailerId(null);
    };

    return (
        <Container fluid>
            <div className="d-flex justify-content-between align-items-center mb-4">
                {view === 'list' ? (
                    <h1 className="h3">
                        {isSalesRep && !isAdmin && !isManager
                            ? 'Mes clients détaillants'
                            : 'Gestion des détaillants'
                        }
                    </h1>
                ) : (
                    <div className="d-flex align-items-center">
                        <Button
                            variant="link"
                            className="p-0 me-2"
                            onClick={handleCancel}
                        >
                            <ArrowLeft size={20} />
                        </Button>
                        <h1 className="h3 mb-0">
                            {view === 'form'
                                ? (selectedRetailerId ? 'Modifier un détaillant' : 'Ajouter un détaillant')
                                : view === 'assign'
                                    ? 'Assigner un commercial'
                                    : 'Détails du détaillant'
                            }
                        </h1>
                    </div>
                )}

                {canModify && view === 'list' && (
                    <Button
                        variant="primary"
                        onClick={handleAddRetailer}
                    >
                        <PlusCircle size={16} className="me-1" />
                        Nouveau détaillant
                    </Button>
                )}
            </div>

            {view === 'list' && (
                <RetailerList
                    onAddRetailer={handleAddRetailer}
                    onEditRetailer={handleEditRetailer}
                    onViewRetailer={handleViewRetailer}
                    onAssignCommercial={(isAdmin || isManager) ? handleAssignCommercial : null}
                    commercialId={isSalesRep ? user.id : null}
                />
            )}

            {view === 'form' && (
                <RetailerForm
                    retailerId={selectedRetailerId}
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                    commercialId={isSalesRep ? user.id : null}
                />
            )}

            {view === 'details' && (
                <RetailerDetails
                    retailerId={selectedRetailerId}
                    onEdit={handleEditRetailer}
                    onAssignCommercial={(isAdmin || isManager) ? handleAssignCommercial : null}
                    onBack={handleCancel}
                />
            )}

            {view === 'assign' && (
                <div className="card shadow-sm">
                    <div className="card-body">
                        <h5 className="card-title mb-3">Assigner un commercial au détaillant</h5>
                        {/* Ici, vous pourriez inclure un composant d'assignation */}
                        {/* Ce composant serait similaire à la modal dans RetailerList, mais en version page complète */}
                        <div className="d-flex justify-content-end mt-3">
                            <Button variant="secondary" onClick={handleCancel} className="me-2">
                                Annuler
                            </Button>
                            <Button variant="primary" onClick={handleSuccess}>
                                Assigner
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default RetailerManagement;